import { produce } from 'immer';

import { Alerts, Drawers, Loaders, Modals } from '@components';
import { StoreHelper } from '@helpers';

export type Screens = 'login' | 'error' | 'updateCredentials';

export interface BaseState {
	visibleLoader: Loaders | '';
	modals: Partial<Record<Modals, number>>;
	visibleAlert: Alerts | '';
	visibleDrawer: Drawers | '';
	visibleScreen: Screens | '';
}

export const initialBaseState: BaseState = {
	visibleLoader: '',
	modals: {},
	visibleAlert: '',
	visibleDrawer: '',
	visibleScreen: '',
};

export interface UseBaseStoreOutput extends BaseState {
	showLoader: (loaderName: Loaders) => void;
	hideLoader: () => void;
	showModal: (modalName: Modals) => void;
	hideModal: (modalName: Modals) => void;
	showAlert: (alertName: Alerts) => void;
	hideAlert: (alertName: Alerts) => void;
	showDrawer: (drawerName: Drawers) => void;
	hideDrawer: (drawerName: Drawers) => void;
	goto: (screenName: Screens) => void;
}

export const useBaseStore = StoreHelper.createStore<UseBaseStoreOutput>(
	(set) => ({
		...initialBaseState,
		showLoader: function (loaderName: Loaders) {
			set(
				produce((state: BaseState) => ({
					...state,
					visibleLoader: loaderName,
				})),
				false,
				'showLoader'
			);
		},
		hideLoader: function () {
			set(
				produce((state: BaseState) => ({
					...state,
					visibleLoader: '',
				})),
				false,
				'hideLoader'
			);
		},
		showModal: function (modalName: Modals) {
			set(
				produce((state: BaseState) => {
					const modalValue = (state.modals[modalName] || 0) + 1;
					const newState: BaseState = {
						...state,
						modals: {
							...state.modals,
							[modalName]: modalValue,
						},
					};

					return newState;
				}),
				false,
				'showModal'
			);
		},
		hideModal: function (modalName: Modals) {
			set(
				produce((state: BaseState) => {
					const modalValue = (state.modals[modalName] || 1) - 1;
					const newState: BaseState = {
						...state,
						modals: {
							...state.modals,
							[modalName]: modalValue,
						},
					};

					return newState;
				}),
				false,
				'hideModal'
			);
		},
		showAlert: function (alertName: Alerts) {
			set(
				produce((state: BaseState) => ({
					...state,
					visibleAlert: alertName,
				})),
				false,
				'showAlert'
			);
		},
		hideAlert: function (alertName: Alerts) {
			set(
				produce((state: BaseState) => {
					if (state.visibleAlert === alertName) {
						return { ...state, visibleAlert: '' };
					}
				}),
				false,
				'hideAlert'
			);
		},

		showDrawer: function (drawerName: Drawers) {
			set(
				produce((state: BaseState) => ({
					...state,
					visibleDrawer: drawerName,
				})),
				false,
				'showDrawer'
			);
		},
		hideDrawer: function (drawerName: Drawers) {
			set(
				produce((state: BaseState) => {
					if (state.visibleDrawer === drawerName) {
						return { ...state, visibleDrawer: '' };
					}
				}),
				false,
				'hideDrawer'
			);
		},

		goto: function (screenName: Screens) {
			set(
				produce((state: BaseState) => ({ ...state, visibleScreen: screenName })),
				false,
				'goto'
			);
		},
	}),
	{ name: 'Base' }
);
